<template>
  <div class="w-full flex flex-col flex-grow p-1 items-center">
<!-- Inputs -->
    <div class="w-full flex flex-row py-1 gap-1 border-b justify-center flex-wrap-reverse">
<!-- Text input + clear -->
      <div class="w-full flex flex-col max-w-md">
        <div class="w-full flex flex-row items-center space-x-1 max-w-md px-1 py-0.5 flex-nowrap">
          <input
            ref="searchInputRef"
            v-model="q"
            type="search"
            class="w-full"
            :class="inputBorderClass"
            placeholder="Search by project #, name, address, or part #"
            maxlength="128"
            autocomplete="off"
            @paste="onPaste"
            @keydown.enter="onSubmit"
          />
          <button v-if="q" class="bn-icon-only" @click="clearAll" :disabled="isWorking">
            <XIcon />
          </button>
        </div>
      </div>
<!-- Customer chip — primary users only -->
      <div v-if="isPrime" class="w-full max-w-md px-1 py-0.5">
        <div v-if="filters.client?._id" class="w-full flex flex-row items-center space-x-1 px-2 py-0.5 rounded-md">
          <div class="w-auto truncate text-left whitespace-normal break-words font-semibold text-sky mr-3">{{ filters.client?.displayName }}</div>
          <button class="bn-icon-only" @click="clearClient" :disabled="isWorking"><XIcon /></button>
        </div>
        <searchApi v-else apiEndpoint="search/organizations" :displayResult="false" placeholder="Narrow By Customer" @resultSelected="selectClient($event)" :disabled="isWorking" />
      </div>
    </div>
<!-- Loading and Errors -->
    <WorkingAndError :isWorking="isWorking" :error="responseError" @clearError="responseError = null" />
<!-- Constrained column — status bar + scroll region share the same max-w so left edges align -->
    <div class="w-full flex flex-col flex-grow flex-auto h-0 mt-1 items-center">
<!-- Results status bar: count + paging. Left padding matches the row container's p-1 so edges align. -->
      <div v-show="results.length > 0" class="w-full flex-shrink-0 flex flex-row flex-wrap items-center gap-2 px-1 py-0.5 max-w-6xl ">
        <button
          class="bn-text text-[11px] flex-shrink-0 mr-2"
          :class="sortMode === 'projectNumber' ? 'text-sky' : ''"
          :disabled="isWorking"
          @click="toggleSortMode"
        >
          <span class="mr-0.5 text-normal">Sort by:</span> {{ sortMode === 'projectNumber' ? 'Project #' : 'Relevance' }}
        </button>
        <div class="w-auto text-left whitespace-nowrap">{{ countLabel }}</div>
        <button v-if="canLoadMore" class="bn-text text-[11px]" @click="loadMore" :disabled="isWorking">Load More..</button>
        <button v-if="canLoadAll" class="bn-text text-amber text-[11px]" @click="loadAll" :disabled="isWorking">Load All</button>
        <div v-if="showNarrowHint" class="flex-grow text-left whitespace-normal">
          Narrow your search to see more matches.
        </div>
      </div>
<!-- Results scroll region -->
      <div class="w-full flex flex-col flex-grow flex-auto h-0 overflow-y-auto items-center">
        <div class="w-full flex flex-col space-y-1 p-1 max-w-6xl">
<!-- Empty state -->
          <div v-if="!isWorking && results.length === 0 && hasRun" class="w-full p-4 text-center">
            No projects found.
          </div>
<!-- Result rows -->
          <ProjectSearchRow
            v-for="row in results"
            :key="row._id"
            :project="row"
            :can-change-order="canChangeOrder"
            :can-see-financials="canSeeFinancials"
            @addChangeOrder="changeOrderProjectId = $event"
            @open="gotoProject($event)"
          />
        </div>
      </div>
    </div>
<!-- Change order modal -->
    <Modal v-if="changeOrderProjectId" title="Create Change Order" @closeModal="changeOrderProjectId = null">
      <template v-slot:content>
        <ChangeOrder :project-id="changeOrderProjectId" @newSave="onChangeOrderSaved" />
      </template>
    </Modal>
  </div>
</template>

<script>
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue'
import api from '@/api.js'
import global from '@/global'
import { XIcon } from '@heroicons/vue/outline'
import searchApi from '@/components/ui/searchApi.vue'
import ProjectSearchRow from './ProjectSearchRow.vue'
import ChangeOrder from '@/components/projects/changeOrder/ChangeOrder.vue'

const DEBOUNCE_MS = 300
const MIN_AUTO_QUERY_CHARS = 3
const PAGE_SIZE = 200
const DEFAULT_PAGE = 25
const MAX_SKIP = 800

export default {
  components: { XIcon, searchApi, ProjectSearchRow, ChangeOrder },
  setup() {
    const { authenticated } = global

  // Refs
    const q = ref('')
    const results = ref([])
    const filters = ref({ client: {} })
    const resultMeta = ref({ count: 0, approximate: false, hasMore: false, cappedAtLimit: false })
    const isWorking = ref(false)
    const responseError = ref(null)
    const hasRun = ref(false)
    const searchInputRef = ref(null)
    const changeOrderProjectId = ref(null)
    // Sort mode: 'relevance' = score-ranked (default), 'projectNumber' = naturalSort by companyProjectNumber.
    // Server-side so pagination is globally consistent — local sort would scramble order across pages.
    const sortMode = ref('relevance')

    let activeController = null
    let debounceTimer = null

  // Computed
    const isPrime = computed(() => !!authenticated.value?.organization?.orgType?.includes?.('primary'))
    const canChangeOrder = computed(() => isPrime.value && (authenticated.value?.role?.accessModel?.projects ?? 0) > 2)
    // FE mirror of the server gate. Server is authoritative — financial fields aren't sent when this is false.
    const canSeeFinancials = computed(() => isPrime.value && (authenticated.value?.role?.accessModel?.projects ?? 0) >= 4)

    const canQuery = computed(() => q.value.trim().length >= MIN_AUTO_QUERY_CHARS || !!filters.value.client?._id)

    const inputBorderClass = computed(() => {
      const len = q.value.trim().length
      if (len === 0) return 'border-sky-500/40'
      if (len < MIN_AUTO_QUERY_CHARS) return 'border-red-500/40'
      return 'border-green-500/40'
    })

    const countLabel = computed(() => {
      if (!results.value.length) return ''
      const shown = results.value.length
      const total = resultMeta.value.approximate ? `${resultMeta.value.count}+` : resultMeta.value.count
      return `Showing ${shown} of ${total} matches`
    })

    const canLoadMore = computed(() => resultMeta.value.hasMore && !resultMeta.value.cappedAtLimit && results.value.length < MAX_SKIP + PAGE_SIZE)
    const canLoadAll  = computed(() => resultMeta.value.hasMore && !resultMeta.value.cappedAtLimit)
    const showNarrowHint = computed(() => resultMeta.value.cappedAtLimit || (resultMeta.value.hasMore && results.value.length >= MAX_SKIP + PAGE_SIZE))

  // API
    const cancelInFlight = () => {
      if (activeController) {
        try { activeController.abort() } catch (e) { /* ignore */ }
        activeController = null
      }
    }

    const runSearch = async ({ append = false, all = false } = {}) => {
      if (!canQuery.value) {
        results.value = []
        hasRun.value = false
        resultMeta.value = { count: 0, approximate: false, hasMore: false, cappedAtLimit: false }
        return
      }
      cancelInFlight()
      activeController = new AbortController()
      isWorking.value = true
      responseError.value = null
      if (!append) results.value = []
      try {
        const body = {
          q: q.value.trim(),
          clientId: filters.value.client?._id || undefined,
          sortMode: sortMode.value
        }
        if (all) {
          body.loadAll = true
        } else {
          body.limit = append ? PAGE_SIZE : DEFAULT_PAGE
          body.skip = append ? results.value.length : 0
        }
        const res = await api.post('search/projectAtlas', body, { signal: activeController.signal })
        const rows = res.data?.data || []
        results.value = append ? results.value.concat(rows) : rows
        resultMeta.value = {
          count: res.data?.count?.value ?? results.value.length,
          approximate: !!res.data?.count?.approximate,
          hasMore: !!res.data?.hasMore,
          cappedAtLimit: !!res.data?.cappedAtLimit
        }
        hasRun.value = true
      } catch (err) {
        if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') return
        responseError.value = err.response?.data?.error || err.message
      } finally {
        isWorking.value = false
      }
    }

    const loadMore = () => runSearch({ append: true })
    const loadAll  = () => runSearch({ all: true })

    // Silent re-run that preserves the current results until the new ones arrive — used after a
    // change-order save (a new project document was created elsewhere in the result set, so a row
    // patch isn't sufficient — re-issue the same query).
    const silentRefetch = async () => {
      if (!canQuery.value) return
      try {
        const body = {
          q: q.value.trim(),
          clientId: filters.value.client?._id || undefined,
          sortMode: sortMode.value,
          limit: Math.max(DEFAULT_PAGE, results.value.length || DEFAULT_PAGE),
          skip: 0
        }
        const res = await api.post('search/projectAtlas', body, { noRetry: true })
        const rows = res.data?.data || []
        results.value = rows
        resultMeta.value = {
          count: res.data?.count?.value ?? rows.length,
          approximate: !!res.data?.count?.approximate,
          hasMore: !!res.data?.hasMore,
          cappedAtLimit: !!res.data?.cappedAtLimit
        }
        hasRun.value = true
      } catch (err) { /* silent per socket-refetch convention */ }
    }

    // Single-row refresh for socket-driven project updates. Mirrors equipmentSearch's
    // fetchAndReplaceOne. Null response → silent remove from list.
    const fetchAndReplaceOne = async (id) => {
      try {
        const res = await api.post('search/projectAtlas/refresh-row', { _id: id }, { noRetry: true })
        const idx = results.value.findIndex(r => r._id === id)
        if (idx === -1) return
        const fresh = res.data?.data
        if (fresh) results.value.splice(idx, 1, fresh)
        else results.value.splice(idx, 1)
      } catch (err) { /* silent */ }
    }

    const scheduleSearch = () => {
      if (debounceTimer) clearTimeout(debounceTimer)
      debounceTimer = setTimeout(() => {
        if (q.value.trim().length >= MIN_AUTO_QUERY_CHARS) onSubmit()
      }, DEBOUNCE_MS)
    }

  // Helpers
    const gotoProject = (id) => { window.open(`/project/${id}`, '_blank') }

    const resetResults = () => {
      results.value = []
      hasRun.value = false
      resultMeta.value = { count: 0, approximate: false, hasMore: false, cappedAtLimit: false }
      if (debounceTimer) { clearTimeout(debounceTimer); debounceTimer = null }
      cancelInFlight()
    }

  // Watchers
    watch(() => q.value.trim(), (trimmed) => {
      if (trimmed.length >= MIN_AUTO_QUERY_CHARS) {
        scheduleSearch()
      } else {
        if (debounceTimer) { clearTimeout(debounceTimer); debounceTimer = null }
        cancelInFlight()
        if (!canQuery.value && results.value.length) {
          results.value = []
          hasRun.value = false
          resultMeta.value = { count: 0, approximate: false, hasMore: false, cappedAtLimit: false }
        }
      }
    })

    // Toggling sort re-fires the search with the new mode so pagination stays globally consistent.
    watch(sortMode, () => {
      if (canQuery.value) runSearch({ append: false })
    })

  // Lifecycle
    onMounted(() => {
      window.addEventListener('data_change_from_socket', onProjectChange)
      nextTick(() => searchInputRef.value?.focus())
    })
    onBeforeUnmount(() => {
      window.removeEventListener('data_change_from_socket', onProjectChange)
      if (debounceTimer) clearTimeout(debounceTimer)
      cancelInFlight()
    })

  // Handlers
    const onSubmit = () => {
      if (!canQuery.value) return
      if (debounceTimer) { clearTimeout(debounceTimer); debounceTimer = null }
      runSearch()
    }

    const onPaste = () => { setTimeout(onSubmit, 0) }

    const selectClient = (item) => {
      filters.value = { client: item || {} }
      if (q.value.trim().length >= MIN_AUTO_QUERY_CHARS) onSubmit()
      else resetResults()
    }
    const clearClient = () => {
      filters.value = { client: {} }
      if (q.value.trim().length >= MIN_AUTO_QUERY_CHARS) onSubmit()
      else resetResults()
    }

    const clearAll = () => {
      q.value = ''
      results.value = []
      hasRun.value = false
      sortMode.value = 'relevance'
      resultMeta.value = { count: 0, approximate: false, hasMore: false, cappedAtLimit: false }
      if (debounceTimer) { clearTimeout(debounceTimer); debounceTimer = null }
      cancelInFlight()
    }

    const toggleSortMode = () => {
      sortMode.value = sortMode.value === 'relevance' ? 'projectNumber' : 'relevance'
    }

    const onChangeOrderSaved = () => {
      changeOrderProjectId.value = null
      silentRefetch()
    }

    // Socket-driven live-list update. Project-tagged events refresh just the affected rows in place.
    const onProjectChange = (e) => {
      if (e?.detail?.type !== 'project') return
      const data = e.detail.data || {}
      const ids = [data.projectId, ...(Array.isArray(data.projectIds) ? data.projectIds : [])]
        .filter(id => typeof id === 'string' && id.length > 0)
      if (!ids.length || !results.value.length) return
      for (const id of ids) {
        const row = results.value.find(r => r._id === id)
        if (!row) continue
        fetchAndReplaceOne(id)
      }
    }

    return {
      q, results, filters, sortMode,
      isWorking, responseError, hasRun,
      searchInputRef, changeOrderProjectId,
      isPrime, canChangeOrder, canSeeFinancials,
      inputBorderClass, countLabel,
      canLoadMore, canLoadAll, showNarrowHint,
      onSubmit, onPaste,
      selectClient, clearClient,
      loadMore, loadAll,
      clearAll, gotoProject, toggleSortMode,
      onChangeOrderSaved
    }
  }
}
</script>

<style scoped>
input[type="search"]::-webkit-search-cancel-button {
  -webkit-appearance: none;
  display: none;
}
</style>
